import { useEffect, useMemo, useState } from 'react';
import { Tooltip, TipPlacement } from 'ui/tooltip';
import noop from 'utils/noop';
import { Wrapper, Option, Name } from './SegmentedControl.styles';
import { SEGMENTED_CONTROL_OPTION_PREFIX, SEGMENTED_CONTROL_TEST_ID } from './constants';
import {
  SegmentedControlOption,
  SegmentedControlProps,
  SEGMENTED_CONTROL_SIZES,
  SEGMENTED_CONTROL_VARIANTS,
} from './types';
import { iconColor } from './utils';

export const SegmentedControl = ({
  isDisabled = false,
  isFullWidth = false,
  onSelect = noop,
  optionMinWidth,
  options,
  size = SEGMENTED_CONTROL_SIZES.MEDIUM,
  value,
  variant = SEGMENTED_CONTROL_VARIANTS.SOLID,
  ...props
}: SegmentedControlProps) => {
  // Value is the authoritative state
  const [selectedValue, selectValue] = useState(value || options[0]?.value);

  useEffect(() => {
    // This keeps internal state in sync, if parent controls selected value
    if (value) selectValue(value);
  }, [value]);

  const select = (val: any) => {
    selectValue(val);
    // Trigger parent callback, if present
    onSelect(val);
  };

  // Only allow keyboard selection of non-disabled options
  const selectableOptions: SegmentedControlOption[] = useMemo(
    () => options.filter((option: SegmentedControlOption) => !option.disabled),
    [options],
  );
  const currentIdx = () => selectableOptions.findIndex((option) => option.value === selectedValue);
  const increment = () => {
    const next = currentIdx() + 1;
    if (!(next > selectableOptions.length - 1)) select(selectableOptions[next]?.value);
  };
  const decrement = () => {
    const next = currentIdx() - 1;
    if (next >= 0) select(selectableOptions[next]?.value);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowLeft':
        return decrement();
      case 'ArrowRight':
        return increment();
      default:
        break;
    }
  };

  const renderOption = (
    { disabled, key, name, value: val, Icon, ...otherProps }: SegmentedControlOption,
    idx: number,
    isSelected: boolean,
  ) => {
    return (
      <Option
        aria-checked={isSelected}
        aria-disabled={isDisabled || disabled}
        data-size={size}
        data-test={`${SEGMENTED_CONTROL_OPTION_PREFIX}${val}`}
        data-value={val}
        key={key || `ctrl-opt-${idx}`}
        role="radio"
        tabIndex={idx === 0 ? 0 : -1}
        onClick={(e) => {
          if (isDisabled || disabled) {
            e.preventDefault();
          } else {
            select(val);
          }
        }}
        isFullWidth={isFullWidth}
        optionMinWidth={optionMinWidth}
        {...otherProps}
      >
        {Icon && <Icon color={iconColor(isSelected, disabled)} />}
        {name && <Name>{name}</Name>}
      </Option>
    );
  };

  return (
    <Wrapper
      data-test={SEGMENTED_CONTROL_TEST_ID}
      role="radiogroup"
      onKeyDown={onKeyDown}
      variant={variant}
      isFullWidth={isFullWidth}
      {...props}
    >
      {options.map(({ tooltip, ...otherProps }: SegmentedControlOption, idx) => {
        const isSelected = selectedValue === otherProps.value;
        return (
          <Tooltip
            isEnabled={!!tooltip}
            key={`ctrl-opt-tooltip-${otherProps.key || idx}`}
            placement={tooltip?.placement || TipPlacement.Top}
            tooltipContent={tooltip?.content}
          >
            {renderOption({ ...otherProps, variant }, idx, isSelected)}
          </Tooltip>
        );
      })}
    </Wrapper>
  );
};
